import { useEffect, useState } from 'react'
import BigButton from './BigButton'

export type SpotProps = {
  id: string
  label: string
  zone: string
  streetAddress: string
}

const ZONE_OPTIONS = [
  'Fluweelstraat',
  'Fluweelstraat Parkkant',
  'Fluweelpark',
  'Kempstraat',
  'Tortelduifstraat',
  'Linnenstraat',
  'Kempstraat Parking',
] as const

type Props = {
  initial: SpotProps
  mode: 'create' | 'edit'
  busy: boolean
  occupants?: { naam: string }[]
  error?: string | null
  onSave: (props: SpotProps) => void
  onCancel: () => void
  onDelete?: () => void
}

export default function SpotPropsForm({
  initial,
  mode,
  busy,
  occupants,
  error,
  onSave,
  onCancel,
  onDelete,
}: Props) {
  const [id, setId] = useState(initial.id)
  const [label, setLabel] = useState(initial.label)
  const [zone, setZone] = useState(initial.zone)
  const [streetAddress, setStreetAddress] = useState(initial.streetAddress)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    setId(initial.id)
    setLabel(initial.label)
    setZone(initial.zone)
    setStreetAddress(initial.streetAddress)
    setConfirmingDelete(false)
  }, [initial.id, initial.label, initial.zone, initial.streetAddress])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    onSave({ id, label: label || id, zone, streetAddress })
  }

  return (
    <form onSubmit={submit} className="card p-5 shadow-block bg-offwhite">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-xl uppercase tracking-display">
          {mode === 'create' ? 'Nieuw plekje' : `Bewerken — ${initial.id}`}
        </h2>
        {occupants && occupants.length > 0 && (
          <span className="text-sm text-ink/70">
            ✓ geclaimd door <strong>{occupants.map((o) => o.naam).join(', ')}</strong>
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          className="input"
          placeholder="Plek-id (bv. F005, FP38)"
          value={id}
          onChange={(e) => setId(e.target.value)}
          required
        />
        <input
          className="input"
          placeholder="Label (optioneel — anders = id)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <select
          className="input"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          required
        >
          <option value="" disabled>
            Zone kiezen…
          </option>
          {ZONE_OPTIONS.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
          {zone && !ZONE_OPTIONS.includes(zone as typeof ZONE_OPTIONS[number]) && (
            <option value={zone}>{zone} (verouderd)</option>
          )}
        </select>
        <input
          className="input"
          placeholder="Adres (bv. Fluweelstraat 8)"
          value={streetAddress}
          onChange={(e) => setStreetAddress(e.target.value)}
          required
        />
      </div>

      {error && (
        <div className="border-[3px] border-red bg-red/10 px-4 py-2 mb-3 text-sm text-red font-semibold">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <BigButton type="submit" disabled={busy}>
          {busy ? 'Bezig…' : mode === 'create' ? 'Plek opslaan' : 'Wijzigingen opslaan'}
        </BigButton>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-muted underline self-center"
        >
          Annuleer
        </button>
        {mode === 'edit' && onDelete && (
          confirmingDelete ? (
            <div className="ml-auto flex items-center gap-3 self-center">
              <span className="text-sm text-red font-semibold">
                Polygoon "{initial.id}" verwijderen?
              </span>
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                className="px-3 py-1 bg-red text-offwhite font-display tracking-display uppercase border-[3px] border-ink shadow-block-sm disabled:opacity-50"
              >
                {busy ? 'Bezig…' : 'Ja, verwijder'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="text-sm text-muted underline"
              >
                Annuleer
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="ml-auto text-sm text-red underline self-center"
            >
              Verwijder polygoon
            </button>
          )
        )}
      </div>
    </form>
  )
}
